
import React, { Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import HomePage from "./pages/HomePage";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import HowItWorks from "./pages/HowItWorks";
import Solutions from "./pages/Solutions";
import Pricing from "./pages/Pricing";
import PricingComparison from "./pages/PricingComparison";
import Support from "./pages/Support";
import Contact from "./pages/Contact";
import MeetingRecording from "./pages/features/MeetingRecording";
import ActionItems from "./pages/features/ActionItems";
import Index from "./pages/Index";
import { MeetingDetail } from "./pages/MeetingDetail";
import MeetingsList from "./pages/MeetingsList";
import Settings from "./pages/Settings";
import Status from "./pages/Status";
import { ActionItemsPage } from "./pages/ActionItemsPage";
import { ActionItemDetail } from "./pages/ActionItemDetail";
import { ThoughtsList } from "./pages/ThoughtsList";
import { ThoughtDetail } from "./pages/ThoughtDetail";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AppLayout } from "./layouts/AppLayout";
import { MarketingLayout } from "./layouts/MarketingLayout";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Card } from "./components/ui/card";

// Create a new QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Fallback loading component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Card className="p-6">
      <div className="flex items-center space-x-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading...</span>
      </div>
    </Card>
  </div>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<LoadingFallback />}>
        <AuthProvider>
          <ThemeProvider>
            <Router>
              <Routes>
                {/* Public Marketing Routes - Wrapped in MarketingLayout */}
                <Route element={<MarketingLayout />}>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/solutions" element={<Solutions />} />
                  <Route path="/how-it-works" element={<HowItWorks />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/pricing/compare" element={<PricingComparison />} />
                  <Route path="/support" element={<Support />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/features/meeting-recording" element={<MeetingRecording />} />
                  <Route path="/features/action-items" element={<ActionItems />} />
                  <Route path="/about" element={<Navigate to="/how-it-works" replace />} />
                  <Route path="/sign-in" element={<SignIn />} />
                  <Route path="/sign-up" element={<SignUp />} />
                </Route>

                {/* Protected App Routes - All wrapped in AppLayout */}
                <Route
                  path="/app/*"
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <Routes>
                          <Route path="dashboard" element={<Index />} />
                          <Route path="meeting/:id" element={<MeetingDetail />} />
                          <Route path="meetings" element={<MeetingsList />} />
                          <Route path="thoughts" element={<ThoughtsList />} />
                          <Route path="thoughts/:id" element={<ThoughtDetail />} />
                          <Route path="action-items" element={<ActionItemsPage />} />
                          <Route 
                            path="action-items/:id" 
                            element={<ActionItemDetail />} 
                          />
                          <Route path="settings/*" element={<Settings />} />
                          <Route path="status" element={<Status />} />
                          <Route path="/" element={<Navigate to="dashboard" replace />} />
                          {/* Catch-all route for /app/* paths */}
                          <Route path="*" element={<Navigate to="dashboard" replace />} />
                        </Routes>
                      </AppLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Redirects */}
                <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
                {/* Global catch-all route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Router>
          </ThemeProvider>
        </AuthProvider>
      </Suspense>
    </QueryClientProvider>
  );
}

export default App;
